import type { ConflictItem } from './types';

export type ConflictValues = Record<string, string>;
export type ConflictErrors = Record<string, string>;

export const SHORT_TITLE_LIMIT = 70;

/* Every missing field starts on our suggestion, if there is one. */
export function initialValues(item: ConflictItem): ConflictValues {
  return Object.fromEntries(
    (item.conflictAttributes ?? []).map(attribute => [
      attribute.attribute_id.field_name,
      attribute.populate ? String(attribute.populate) : '',
    ]),
  );
}

/*
 * A field is fine once filled (and a short title within its limit). The
 * item can be resolved as soon as any field is: those with an error are
 * left out and stay missing.
 */
export function validate(values: ConflictValues): {
  errors: ConflictErrors;
  isValid: boolean;
} {
  const errors: ConflictErrors = {};
  let isValid = false;

  for (const [key, value] of Object.entries(values)) {
    if (!value) {
      errors[key] = 'Not valid';
    } else if (key === 'short_title' && value.length > SHORT_TITLE_LIMIT) {
      errors[key] = `Should be less than ${SHORT_TITLE_LIMIT}`;
    } else {
      isValid = true;
    }
  }

  return { errors, isValid };
}

export type ResolvePayload = Record<string, unknown> & { item_id: string };

/*
 * What the resolve call sends for one item. "Ignore" marks the UPC as not
 * applicable and passes our suggested identifier along with it.
 */
export function resolvePayload(
  item: ConflictItem,
  values: ConflictValues,
  notApplicable: boolean,
): { payload: ResolvePayload; resolvedKeys: string[] } {
  const { errors } = notApplicable
    ? { errors: {} as ConflictErrors }
    : validate(values);
  const resolved = Object.fromEntries(
    Object.entries(values).filter(([key]) => !errors[key]),
  );
  const payload: ResolvePayload = { ...resolved, item_id: item._id };

  if (notApplicable) {
    payload.NA = true;
    const suggestion = item.conflictAttributes?.[0]?.populate;
    if (suggestion) payload.upc = suggestion;
  }

  return { payload, resolvedKeys: Object.keys(resolved) };
}
